import React, { createContext, useContext, useEffect, useState } from 'react';
import { authService } from '../services/authService';
import type { User, LoginCredentials, ApiResponse, AuthResponse } from '../types';

interface AdminAuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<ApiResponse<AuthResponse>>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  hasRole: (role: 'user' | 'workspace_admin' | 'super_admin') => boolean;
  hasPermission: (permission: string) => boolean;
  isSuperAdmin: () => boolean;
  isWorkspaceAdmin: () => boolean;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      if (authService.isAuthenticated()) {
        try {
          const currentUser = await authService.getCurrentUser();
          setUser(currentUser);
        } catch (error) {
          console.error('Failed to get current user:', error);
          await authService.logout();
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (credentials: LoginCredentials): Promise<ApiResponse<AuthResponse>> => {
    setIsLoading(true);
    try {
      console.log('useAdminAuth: Calling authService.login');
      const response = await authService.login(credentials);
      console.log('useAdminAuth: Login response', response);
      
      if (response.success && response.data) {
        console.log('useAdminAuth: Setting user', response.data.user);
        setUser(response.data.user);
      } else {
        console.warn('useAdminAuth: Login not successful', response);
      }
      return response;
    } catch (error) {
      console.error('useAdminAuth: Login error', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Login failed'
      };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    setIsLoading(true);
    try {
      await authService.logout();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUser = async (): Promise<void> => {
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const hasRole = (role: 'user' | 'workspace_admin' | 'super_admin'): boolean => {
    return authService.hasRole(role);
  };

  const hasPermission = (permission: string): boolean => {
    return authService.hasPermission(permission);
  };

  const isSuperAdmin = (): boolean => {
    return authService.isSuperAdmin();
  };

  const isWorkspaceAdmin = (): boolean => {
    return authService.isWorkspaceAdmin();
  };

  const value: AdminAuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshUser,
    hasRole,
    hasPermission,
    isSuperAdmin,
    isWorkspaceAdmin,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = (): AdminAuthContextType => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};
















